import { EProductWithVariants } from '@gelato-api-ui/core/e-commerce/e-product-with-variants';
import { EProductSearchRequest } from '@gelato-api-ui/core/e-commerce/e-product-search-request';
import { EProductSearchResponse } from '@gelato-api-ui/core/e-commerce/e-product-search-response';
import { InitialProductVariantConnectionData } from '@gelato-api-ui/core/e-commerce/initial-product-variant-connection-data';
import * as actions from './e-commerce-products.actions';
import { EProductCollection } from '@gelato-api-ui/core/e-commerce/e-product-collection';
import { EProductAbbridged } from '@gelato-api-ui/core/e-commerce/e-product-abbridged';
import { Tag } from '@gelato-api-ui/ui-kit/src/lib/tag/tag';

export interface State {
  productsList: ProductsListState;
  product: ProductState;
  initialProductVariantConnectionData: InitialProductVariantConnectionData;
  tags: { [storeId: string]: Tag[] };
  collections: { [storeId: string]: EProductCollection[] };
  saveProduct: boolean;
  productDuplicationAvailability: ProductDuplicationAvailabilityFlagsCollection;
}

export interface ProductsListState {
  isLoading: boolean;
  request: EProductSearchRequest;
  payload: EProductSearchResponse;
}

export interface ProductState {
  isLoading: boolean;
  payload: EProductWithVariants;
}

export interface ProductDuplicationAvailabilityFlagsCollection {
  [productId: string]: boolean;
}

export const initialState: State = {
  productsList: {
    isLoading: false,
    request: null,
    payload: null,
  },
  product: {
    isLoading: false,
    payload: null,
  },
  initialProductVariantConnectionData: null,
  tags: null,
  collections: null,
  saveProduct: false,
  productDuplicationAvailability: {},
};

export function reducer(state = initialState, action: actions.ECommerceProductsActions): State {
  switch (action.type) {
    case actions.ECommerceProductsActionTypes.ResetProductsListState:
      return {
        ...state,
        productsList: { ...initialState.productsList },
      };
    case actions.ECommerceProductsActionTypes.ResetProductsListPayload:
      return {
        ...state,
        productsList: {
          ...state.productsList,
          payload: null,
        },
      };
    case actions.ECommerceProductsActionTypes.ResetProductState:
      return {
        ...state,
        product: { ...initialState.product },
      };
    case actions.ECommerceProductsActionTypes.SetProductsListState:
      return {
        ...state,
        productsList: {
          ...action.payload,
          payload: {
            ...action.payload.payload,
            products: action.payload.payload
              ? action.payload.payload.products.map(pr => ({
                  ...pr,
                  externalThumbnailUrl: pr.externalThumbnailUrl || pr.previewUrl,
                }))
              : null,
          },
        },
      };
    case actions.ECommerceProductsActionTypes.SetProductState:
      return {
        ...state,
        product: action.payload,
      };
    case actions.ECommerceProductsActionTypes.SetInitialProductVariantConnectionData:
      return {
        ...state,
        initialProductVariantConnectionData: action.payload,
      };
    case actions.loadProductTagsSuccess.type:
      const _tags: Tag[] = [];
      for (const [key, value] of Object.entries(action.tags)) {
        _tags.push({
          id: key,
          title: value,
        });
      }
      return {
        ...state,
        tags: {
          ...state.tags,
          [action.storeId]: _tags,
        },
      };
    case actions.loadProductCollectionsSuccess.type:
      return {
        ...state,
        collections: {
          ...state.collections,
          [action.storeId]: action.collections,
        },
      };
    case actions.addProducts.type: {
      const storeProducts = state?.productsList?.payload?.products || [];
      const storeProductIds = storeProducts.map(loopProduct => loopProduct.id);
      const newProducts = action.products.filter(loopProduct => !storeProductIds.includes(loopProduct.id));
      const updatedProducts = action.products.filter(loopProduct => storeProductIds.includes(loopProduct.id));
      const products = [
        ...newProducts,
        ...storeProducts.map(
          loopStoreProduct =>
            updatedProducts.find(loopProduct => loopStoreProduct.id === loopProduct.id) || loopStoreProduct,
        ),
      ];

      return {
        ...state,
        productsList: {
          ...state.productsList,
          payload: { products },
        },
      };
    }
    case actions.updateProduct.type: {
      const { id, payload, forcePreviewUpdate } = action;
      const storeProducts = state?.productsList?.payload?.products || [];
      const products = storeProducts.map((loopProduct: EProductAbbridged): EProductAbbridged => {
        if (loopProduct?.id !== id) {
          return loopProduct;
        }

        // Fix for blinking previews
        const externalThumbnailUrl =
          forcePreviewUpdate || !loopProduct.externalThumbnailUrl
            ? payload.externalThumbnailUrl
            : loopProduct.externalThumbnailUrl;

        return {
          ...loopProduct,
          ...payload,
          externalThumbnailUrl,
        };
      });

      return {
        ...state,
        productsList: {
          ...state.productsList,
          payload: { products },
        },
      };
    }
    case actions.removeProducts.type: {
      const { productIds } = action;
      const storeProducts = state?.productsList?.payload?.products || [];

      return {
        ...state,
        productsList: {
          ...state.productsList,
          payload: {
            products: storeProducts.filter(
              (loopProduct: EProductAbbridged): boolean => !productIds.includes(loopProduct?.id),
            ),
          },
        },
      };
    }

    case actions.saveProduct.type:
      return {
        ...state,
        saveProduct: true,
      };

    case actions.saveProductStart.type:
      return {
        ...state,
        saveProduct: false,
      };

    case actions.clearCanDuplicateProductAsDraftFlags.type:
      return {
        ...state,
        productDuplicationAvailability: initialState.productDuplicationAvailability,
      };
    case actions.setProductDuplicationAvailabilityFlag.type:
      return {
        ...state,
        productDuplicationAvailability: {
          ...state.productDuplicationAvailability,
          [action.productId]: action.payload,
        },
      };
    default:
      return state;
  }
}
